import React, { useEffect, useState, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Download, X, FileText, Mail } from 'lucide-react';
import Layout from '../components/Layout';
import InvoiceTemplate from '../components/InvoiceTemplate';
import ContractPreview from '../components/ContractPreview';
import { useToast } from '../components/ToastProvider';
import { downloadPDFFromElement } from '../utils/pdfGenerator';
import { getLogo } from '../utils/logoUpload';
import { invoiceAPI, customerAPI, api, companyProfileAPI } from '../api/client';

const InvoicePreview = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const invoiceRef = useRef();
  const contractRef = useRef();
  const emailStatusResetRef = useRef(null);
  const { showToast } = useToast();

  const [invoice, setInvoice] = useState(null);
  const [customer, setCustomer] = useState({});
  const [company, setCompany] = useState({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [emailStatus, setEmailStatus] = useState('idle');
  const [showContract, setShowContract] = useState(false);

  useEffect(() => {
    return () => {
      if (emailStatusResetRef.current) {
        clearTimeout(emailStatusResetRef.current);
      }
    };
  }, []);

  const loadInvoiceData = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      // Load invoice
      const invoiceResponse = await invoiceAPI.get(id);
      const invoiceData = invoiceResponse.data;
      setInvoice(invoiceData);

      // Load customer
      if (invoiceData.customer_id) {
        try {
          const customerResponse = await customerAPI.get(invoiceData.customer_id);
          // Axios wraps response in { data: ... }, extract it
          const customerData = customerResponse.data;
          setCustomer(customerData || {});
          console.log('Customer loaded:', customerData);
        } catch (err) {
          console.error('Error loading customer:', err);
          setCustomer({ name: `Customer ${invoiceData.customer_id}` });
        }
      }

      // Load company info, but don't block invoice preview if this fails.
      try {
        const profileResponse = await companyProfileAPI.get();
        const logo = getLogo();
        setCompany({
          company: profileResponse.data.companyName || '',
          email: profileResponse.data.email || '',
          phone: profileResponse.data.phone || '',
          address: profileResponse.data.address || '',
          city: profileResponse.data.city || '',
          state: profileResponse.data.state || '',
          zipCode: profileResponse.data.zipCode || '',
          country: profileResponse.data.country || '',
          logo: logo || undefined,
        });
      } catch (companyErr) {
        console.error('Error loading company profile:', companyErr);
        setCompany({
          logo: getLogo() || undefined,
        });
      }
    } catch (err) {
      console.error('Error loading invoice data:', err);
      setError('Failed to load invoice. Please try again.');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    loadInvoiceData();
  }, [loadInvoiceData]);

  const handleExportPDF = async () => {
    let elementToExport = invoiceRef.current;
    
    // If contract is shown, export both invoice and contract together
    if (showContract && contractRef.current) {
      // Create a wrapper for both
      const wrapper = document.createElement('div');
      wrapper.innerHTML = invoiceRef.current.innerHTML + contractRef.current.innerHTML;
      elementToExport = wrapper;
    }

    if (!elementToExport) return;

    setExporting(true);
    try {
      const filename = `Invoice-${invoice.invoice_number || invoice.id}`;
      await downloadPDFFromElement(elementToExport, filename);
      showToast({
        title: 'PDF exported',
        message: `${filename}.pdf was generated successfully.`,
        tone: 'success',
      });
    } catch (err) {
      console.error('Error exporting PDF:', err);
      showToast({
        title: 'Export failed',
        message: 'The PDF could not be exported. Please try again.',
        tone: 'error',
      });
    } finally {
      setExporting(false);
    }
  };

  const handleSendEmail = async () => {
    if (!customer.email) {
      showToast({
        title: 'Missing customer email',
        message: 'Add an email address to the customer profile before sending the invoice.',
        tone: 'error',
      });
      return;
    }

    if (!invoiceRef.current) {
      showToast({
        title: 'Invoice unavailable',
        message: 'The invoice preview could not be accessed for email export.',
        tone: 'error',
      });
      return;
    }

    if (emailStatusResetRef.current) {
      clearTimeout(emailStatusResetRef.current);
      emailStatusResetRef.current = null;
    }

    setEmailStatus('sending');
    try {
      // Generate PDF from invoice element
      const html2canvas = (await import('html2canvas')).default;
      const jsPDF = (await import('jspdf')).default;

      const canvas = await html2canvas(invoiceRef.current, {
        scale: 1.25,
        useCORS: true,
        logging: false,
        backgroundColor: '#ffffff',
      });

      const imgData = canvas.toDataURL('image/jpeg', 0.82);
      const pdf = new jsPDF({
        orientation: 'portrait',
        unit: 'mm',
        format: 'a4',
      });

      const pageHeight = pdf.internal.pageSize.getHeight();
      const pageWidth = pdf.internal.pageSize.getWidth();
      const imgHeight = (canvas.height * pageWidth) / canvas.width;
      let heightLeft = imgHeight;
      let position = 0;

      pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight, undefined, 'MEDIUM');
      heightLeft -= pageHeight;

      while (heightLeft >= 0) {
        position = heightLeft - imgHeight;
        pdf.addPage();
        pdf.addImage(imgData, 'JPEG', 0, position, pageWidth, imgHeight, undefined, 'MEDIUM');
        heightLeft -= pageHeight;
      }

      // Convert PDF to base64
      const pdfDataUri = pdf.output('datauristring');
      const pdfBase64 = pdfDataUri.split(',')[1];

      // Send email with PDF via backend API
      await api.post(`/invoices/${id}/send-email`, {
        customer_email: customer.email,
        pdfBase64,
      });

      setEmailStatus('sent');
      emailStatusResetRef.current = setTimeout(() => {
        setEmailStatus('idle');
        emailStatusResetRef.current = null;
      }, 2000);
      showToast({
        title: 'Invoice sent',
        message: `The invoice was emailed successfully to ${customer.email}.`,
        tone: 'success',
      });
    } catch (err) {
      console.error('Error sending email:', err);
      setEmailStatus('idle');
      showToast({
        title: 'Email failed',
        message: 'The invoice email could not be sent. Check the customer email and your server mail settings.',
        tone: 'error',
      });
    }
  };

  if (loading) {
    return (
      <Layout pageTitle="Invoice Preview" pageSubtitle="View and export invoice">
        <div className="flex items-center justify-center h-64">
          <div className="text-center">
            <div className="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4"></div>
            <p className="text-gray-600">Loading invoice...</p>
          </div>
        </div>
      </Layout>
    );
  }

  if (error) {
    return (
      <Layout pageTitle="Invoice Preview" pageSubtitle="View and export invoice">
        <div className="max-w-4xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-4">
            <p className="text-red-700">{error}</p>
          </div>
          <button
            onClick={() => navigate('/invoices')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Invoices
          </button>
        </div>
      </Layout>
    );
  }

  if (!invoice) {
    return (
      <Layout pageTitle="Invoice Preview" pageSubtitle="View and export invoice">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-gray-600 mb-4">Invoice not found</p>
          <button
            onClick={() => navigate('/invoices')}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Back to Invoices
          </button>
        </div>
      </Layout>
    );
  }

  return (
    <Layout pageTitle="Invoice Preview" pageSubtitle="View and export invoice">
      <div className="max-w-5xl mx-auto">
        {/* Action Buttons */}
        <div className="flex gap-3 mb-6 justify-between items-center no-print">
          <div className="flex gap-3 flex-wrap">
            <button
              onClick={handleExportPDF}
              disabled={exporting}
              className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 transition font-medium"
            >
              <Download size={18} />
              {exporting ? 'Exporting...' : 'Export as PDF'}
            </button>
            <button
              onClick={() => setShowContract(!showContract)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg transition font-medium ${
                showContract
                  ? 'bg-purple-600 text-white hover:bg-purple-700'
                  : 'bg-gray-200 text-gray-800 hover:bg-gray-300'
              }`}
            >
              <FileText size={18} />
              {showContract ? 'Hide Contract' : 'View Contract'}
            </button>
            <button
              onClick={handleSendEmail}
              disabled={exporting || emailStatus === 'sending' || !customer.email}
              title={!customer.email ? 'Customer email not found' : 'Send invoice to customer email'}
              className="flex items-center gap-2 px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed transition font-medium"
            >
              <Mail size={18} />
              {emailStatus === 'sending' ? 'Sending Email' : emailStatus === 'sent' ? 'Sent' : 'Send Email'}
            </button>
          </div>
          <button
            onClick={() => navigate('/invoices')}
            className="flex items-center gap-2 px-4 py-2 bg-gray-300 text-gray-800 rounded-lg hover:bg-gray-400 transition font-medium"
          >
            <X size={18} />
            Close
          </button>
        </div>

        {/* Invoice Template */}
        <div className="bg-gray-50 p-4 rounded-lg">
          <div ref={invoiceRef}>
            <InvoiceTemplate
              invoice={invoice}
              customer={customer}
              company={company}
            />
          </div>
        </div>

        {/* Contract Section */}
        {showContract && invoice.project_type && (
          <div className="bg-gray-50 p-4 rounded-lg mt-6" ref={contractRef}>
            <ContractPreview
              projectType={invoice.project_type}
              companyName={company.company}
              customerName={customer.name}
            />
          </div>
        )}

        {/* Footer Info */}
        <div className="mt-6 text-center text-sm text-gray-600 no-print">
          <p>Use "Export as PDF" to download or "Print" to save as PDF from your browser</p>
          {invoice.project_type && <p className="mt-2 text-xs text-purple-600">📄 This invoice includes a {invoice.project_type.replace(/_/g, ' ')} contract</p>}
        </div>
      </div>

      <style>{`
        @media print {
          .no-print {
            display: none;
          }
          
          body {
            background: white;
          }
          
          .layout-wrapper {
            padding: 0;
          }
        }
      `}</style>
    </Layout>
  );
};

export default InvoicePreview;
